/**
 * /dashboard/accounting/reports/profit-and-loss — P&L (G_ACC8).
 *
 * Period (from / to) picker + Schedule III Part II grouped layout +
 * CSV export. Revenue from Operations gets its own section ahead of
 * Other Income; expense groups follow the canonical Schedule III
 * ordering with tenant-custom heads alphabetised after.
 *
 * Migration note (Phase 3e — 2026-06-01): port of
 * frontend/src/app/(dashboard)/dashboard/accounting/reports/profit-and-loss/page.tsx.
 */

import { useState } from "react";
import { createFileRoute, Link , useParams} from "@tanstack/react-router";
import { ArrowLeft, Download } from "lucide-react";
import { AccountingModuleShell } from "@/components/shared/accounting-module-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import { formatAccountsAmount as formatPaise } from "@/lib/accounting-gst";
import { LedgerLink } from "@/components/accounting/ledger-link";
import { formatDateRange } from "@/lib/format";
import { profitAndLossCSVURL } from "@/lib/api/accounting";
import { useProfitAndLossReport } from "@/hooks/use-accounting";
import { toApiError } from "@/lib/api";
import type { PLLineGroup } from "@/types/accounting";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/accounting/reports/profit-and-loss",
)({
  component: ProfitAndLossRoute,
});

// Default window = current FY-to-date (Apr 1 of the current FY → today, UTC).
function defaultFYToDate(): { from: string; to: string } {
  const d = new Date();
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth() + 1; // 1..12
  const fyStart = m >= 4 ? y : y - 1;
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  return {
    from: `${fyStart}-04-01`,
    to: `${yyyy}-${mm}-${dd}`,
  };
}

function GroupSection({
  group,
  emptyLabel,
  from,
  to,
}: Readonly<{ group: PLLineGroup; emptyLabel?: string; from: string; to: string }>) {
  if (group.lines.length === 0 && !emptyLabel) return null;
  return (
    <div className="rounded-md border">
      <div className="border-b bg-muted/40 px-4 py-2 text-sm font-medium">
        {group.label}
      </div>
      <div className="divide-y">
        {group.lines.length === 0 ? (
          <div className="px-4 py-3 text-sm text-muted-foreground">
            {emptyLabel}
          </div>
        ) : (
          group.lines.map((l) => (
            <div
              key={l.account_id}
              className="grid grid-cols-[5rem_1fr_auto] gap-3 px-4 py-2 text-sm"
            >
              <span className="font-mono text-xs text-muted-foreground">
                {l.account_code}
              </span>
              <span>{l.account_name}</span>
              <span className="tabular-nums">
                <LedgerLink accountId={l.account_id} from={from} to={to} label={`Ledger of ${l.account_name}`}>
                  {formatPaise(l.amount_minor)}
                </LedgerLink>
              </span>
            </div>
          ))
        )}
        <div className="grid grid-cols-[5rem_1fr_auto] gap-3 bg-muted/20 px-4 py-2 text-sm font-medium">
          <span />
          <span>{group.label} — Subtotal</span>
          <span className="tabular-nums">
            {formatPaise(group.subtotal_minor)}
          </span>
        </div>
      </div>
    </div>
  );
}

function ProfitAndLossRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const init = defaultFYToDate();
  const [from, setFrom] = useState<string>(init.from);
  const [to, setTo] = useState<string>(init.to);
  const [pendFrom, setPendFrom] = useState<string>(init.from);
  const [pendTo, setPendTo] = useState<string>(init.to);
  const query = useProfitAndLossReport(from, to);
  const data = query.data ?? null;
  const loading = query.isPending;
  const error = query.isError ? toApiError(query.error).message : null;

  return (
    <AccountingModuleShell
      title="Profit and Loss"
      description="Revenue, expenses, and net profit — Schedule III Part II grouping."
      actions={
        <div className="flex items-center gap-2">
          <Button asChild variant="outline" className="gap-1.5">
            <Link to="/app/$tenantSlug/accounting/reports" params={{ tenantSlug }}>
              <ArrowLeft className="h-4 w-4" />
              Back
            </Link>
          </Button>
          <Button asChild className="gap-1.5">
            <a href={profitAndLossCSVURL(from, to)} download>
              <Download className="h-4 w-4" />
              Export CSV
            </a>
          </Button>
        </div>
      }
    >
      <Card>
        <CardContent className="grid gap-3 py-4 sm:grid-cols-[1fr_1fr_auto]">
          <div className="grid gap-1.5">
            <Label htmlFor="pl-from">From</Label>
            <Input
              id="pl-from"
              type="date"
              value={pendFrom}
              onChange={(e) => setPendFrom(e.target.value)}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="pl-to">To</Label>
            <Input
              id="pl-to"
              type="date"
              value={pendTo}
              onChange={(e) => setPendTo(e.target.value)}
            />
          </div>
          <div className="flex items-end">
            <Button
              type="button"
              onClick={() => {
                setFrom(pendFrom);
                setTo(pendTo);
              }}
              disabled={
                !pendFrom ||
                !pendTo ||
                (pendFrom === from && pendTo === to) ||
                pendTo < pendFrom
              }
            >
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {error && (
        <Alert variant="destructive" className="mt-4">
          <AlertTitle>Could not load profit and loss</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading ? (
        <div className="mt-4 space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-10" />
          ))}
        </div>
      ) : data ? (
        <div className="mt-4 space-y-4">
          <GroupSection
            group={data.revenue}
            from={data.period_start}
            to={data.period_end}
            emptyLabel="No revenue from operations posted in the period."
          />
          <GroupSection
            group={data.other_income}
            from={data.period_start}
            to={data.period_end}
            emptyLabel="No other income posted in the period."
          />
          <Card>
            <CardContent className="flex justify-between py-3 text-sm font-medium">
              <span>Total Income</span>
              <span className="tabular-nums">
                {formatPaise(data.total_income_minor)}
              </span>
            </CardContent>
          </Card>

          {data.expenses.map((g) => (
            <GroupSection key={g.label} group={g} from={data.period_start} to={data.period_end} />
          ))}
          <Card>
            <CardContent className="flex justify-between py-3 text-sm font-medium">
              <span>Total Expenses</span>
              <span className="tabular-nums">
                {formatPaise(data.total_expenses_minor)}
              </span>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="grid gap-2 py-4 text-sm">
              <div className="flex justify-between">
                <span>Profit Before Tax</span>
                <span className="tabular-nums">
                  {formatPaise(data.profit_before_tax_minor)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Tax Expense</span>
                <span className="tabular-nums">
                  {formatPaise(data.tax_expense_minor)}
                </span>
              </div>
              <div className="mt-2 flex justify-between border-t pt-2 text-base font-semibold">
                <span>Net Profit / (Loss)</span>
                <span className="tabular-nums">
                  {formatPaise(data.net_profit_minor)}
                </span>
              </div>
            </CardContent>
          </Card>

          <p className="text-xs text-muted-foreground">
            Period {formatDateRange(data.period_start, data.period_end)}.
          </p>
        </div>
      ) : null}
    </AccountingModuleShell>
  );
}
